// PRD.319 — Visit Note Investigations.
//
// Encounter documentation of a diagnostic investigation:
//
//     16        IOPA   Periapical radiolucency…   🎙  ×
//     General   OPG    No significant pathology…  🎙  ×
//     + Add Investigation
//
// NOT Laboratory: `lab_orders` / `lab_results` model an order→result workflow.
// This is a clinical note, and the two stay separate (PRD §18).
//
// NOT an image: an investigation is the clinician's interpretation and works
// with no attachment at all. `patient_files` remains the only image store and
// is not duplicated or linked here — `patient_files` has no tooth metadata, so
// a meaningful relation would need new file architecture, deferred per §17.
//
// NO INFERENCE: chart context may preselect a TOOTH, never an investigation
// type (§14); findings never create a Diagnosis (§15) or a Treatment (§16).
//
// Reuse: `HistoryEntryEditor` (PRD.314/316) for the autosaving, dictating
// findings field with explicit-newline sizing; `ToothPickerModal` (PRD.316);
// `summarizeDiagnosisTeeth` (PRD.317) for `16 · 17 · 18 +3`; and the tooth-link
// helpers from PRD.318 so all four dental features share one implementation.

import { useCallback, useMemo, useRef, useState } from 'react';
import {
  buildChartSuggestions,
  dentalToothLinkId,
  diffTeeth,
  formatSurfaceInitials,
  getMedicalHistorySettings,
  groupToothLinks,
  parseToothStatesMap,
  qAppConfig,
  qDentalInvestigationTeethByPatient,
  qDentalInvestigationsByEncounter,
  qOdontogramChartsByEncounter,
  resolveInvestigationType,
  searchInvestigationTypes,
  selectEncounterInvestigations,
  summarizeDiagnosisTeeth,
  type AppConfigRow,
  type DentalInvestigationRow,
  type OdontogramChartRow,
  type PatientEncounterRow,
} from '@helios/data-access';
import { useAuth } from '@helios/auth-firebase-web';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { NavIcon, useBrandTokens } from '../../../shell/shared';
import { HistoryEntryEditor } from './HistoryEntryEditor';
import { ToothPickerModal } from './ToothPickerModal';

/** Border + padding above a compact field's first line — see ClinicalFindingsSection. */
const FIRST_LINE_OFFSET = 7;

export function DentalInvestigationsSection({
  encounter,
  patientId,
}: {
  encounter: PatientEncounterRow;
  patientId: string;
}) {
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';

  const [toothPickerFor, setToothPickerFor] = useState<string | null>(null);
  /**
   * The one unpersisted row "+ Add Investigation" opens. It becomes a record on
   * its first meaningful save, so an abandoned click never leaves a blank
   * investigation behind — the PRD.316 rule.
   */
  const [draft, setDraft] = useState<{ id: string; teeth: string[] } | null>(null);
  const pendingRef = useRef(new Map<string, { teeth: string[] }>());

  // ── Reads ──

  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const voiceSettings = useMemo(
    () => getMedicalHistorySettings(appConfigRows[0] ?? null),
    [appConfigRows],
  );

  const { data: rows = [] } = usePowerSyncQuery<DentalInvestigationRow>(
    qDentalInvestigationsByEncounter(encounter.id),
  );
  const investigations = useMemo(
    () => selectEncounterInvestigations(rows, encounter.id),
    [rows, encounter.id],
  );

  const { data: toothRows = [] } = usePowerSyncQuery<{
    id: string;
    tooth: string | null;
    dental_investigation_id: string | null;
  }>(qDentalInvestigationTeethByPatient(patientId));
  const teethByInvestigation = useMemo(
    () => groupToothLinks(toothRows, (r) => r.dental_investigation_id),
    [toothRows],
  );

  // Chart context, for tooth suggestions and for the picker's visual state.
  const { data: chartRows = [] } = usePowerSyncQuery<OdontogramChartRow>(
    qOdontogramChartsByEncounter(encounter.id),
  );
  const toothStates = useMemo(
    () => (chartRows[0] ? parseToothStatesMap(chartRows[0].tooth_states) : {}),
    [chartRows],
  );

  /**
   * Chart context offered as one-tap tooth preselection.
   *
   * TOOTH AND SURFACE ONLY. The condition label rides along so the clinician
   * knows why the tooth is listed, but tapping it NEVER chooses an
   * investigation type — no caries→IOPA, impacted→CBCT or missing→OPG (§14).
   * The type field opens empty every time.
   */
  const suggestions = useMemo(() => {
    const out: { ref: string; tooth: string; surfaces: string; label: string }[] = [];
    for (const chart of chartRows) {
      for (const s of buildChartSuggestions(chart.id, parseToothStatesMap(chart.tooth_states))) {
        out.push({
          ref: s.ref,
          tooth: s.tooth,
          surfaces: formatSurfaceInitials(s.surfaces),
          label: s.label,
        });
      }
    }
    return out;
  }, [chartRows]);

  // ── Writes ──

  const insertToothLinks = useCallback(
    async (investigationId: string, teeth: readonly string[]) => {
      if (!db) return;
      const now = new Date().toISOString();
      for (const tooth of teeth) {
        await db.execute(
          `INSERT INTO dental_investigation_teeth
             (id, org_id, dental_investigation_id, patient_id, tooth, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?)`,
          [
            dentalToothLinkId(investigationId, tooth),
            orgId,
            investigationId,
            patientId,
            tooth,
            now,
            now,
          ],
        );
      }
    },
    [db, orgId, patientId],
  );

  /** Persist a tooth selection as a minimal diff — unchanged teeth write nothing. */
  const saveTeeth = useCallback(
    async (investigationId: string, next: string[]) => {
      if (!db) return;
      const current = teethByInvestigation.get(investigationId) ?? [];
      const { added, removed } = diffTeeth(current, next);
      for (const tooth of removed) {
        await db.execute(`DELETE FROM dental_investigation_teeth WHERE id = ?`, [
          dentalToothLinkId(investigationId, tooth),
        ]);
      }
      await insertToothLinks(investigationId, added);
    },
    [db, teethByInvestigation, insertToothLinks],
  );

  /**
   * Persist the investigation type. A draft is INSERTed on its first meaningful
   * value, together with any preselected teeth.
   *
   * `findings` is deliberately NOT required to create the row (§10): a
   * clinician may record that an OPG was taken before dictating what it showed.
   */
  const saveType = useCallback(
    async (text: string, id: string) => {
      if (!db) return;
      const trimmed = text.trim();
      const now = new Date().toISOString();
      const pending = pendingRef.current.get(id);

      if (pending) {
        if (!trimmed) return;
        await db.execute(
          `INSERT INTO dental_investigations
             (id, org_id, clinic_id, patient_id, encounter_id, type, findings,
              created_by_user_id, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [id, orgId, null, patientId, encounter.id, trimmed, null, user?.uid ?? null, now, now],
        );
        if (pending.teeth.length > 0) await insertToothLinks(id, pending.teeth);
        pendingRef.current.delete(id);
        setDraft((d) => (d?.id === id ? null : d));
        return;
      }

      if (!trimmed) return;
      await db.execute(`UPDATE dental_investigations SET type = ?, updated_at = ? WHERE id = ?`, [
        trimmed,
        now,
        id,
      ]);
    },
    [db, orgId, patientId, encounter.id, user?.uid, insertToothLinks],
  );

  /** Findings attach to a saved row only — a draft has nothing to write them to. */
  const saveFindings = useCallback(
    async (text: string, id: string) => {
      if (!db || pendingRef.current.has(id)) return;
      await db.execute(
        `UPDATE dental_investigations SET findings = ?, updated_at = ? WHERE id = ?`,
        [text.trim() ? text : null, new Date().toISOString(), id],
      );
    },
    [db],
  );

  const startDraft = useCallback((teeth: string[] = []) => {
    const id = crypto.randomUUID();
    pendingRef.current.set(id, { teeth: [...teeth] });
    setDraft({ id, teeth: [...teeth] });
  }, []);

  const removeInvestigation = useCallback(
    async (id: string) => {
      if (pendingRef.current.has(id)) {
        pendingRef.current.delete(id);
        setDraft((d) => (d?.id === id ? null : d));
        return;
      }
      if (!db) return;
      try {
        await db.execute(
          `DELETE FROM dental_investigation_teeth WHERE dental_investigation_id = ?`,
          [id],
        );
      } catch (err) {
        console.warn('[dental-investigations] could not clear tooth scope', err);
      }
      await db.execute(`DELETE FROM dental_investigations WHERE id = ?`, [id]);
    },
    [db],
  );

  // ── Render ──

  const displayRows = useMemo(() => {
    const list = investigations.map((r) => ({ row: r, isDraft: false }));
    if (draft && !investigations.some((r) => r.id === draft.id)) {
      list.push({ row: { id: draft.id } as DentalInvestigationRow, isDraft: true });
    }
    return list;
  }, [investigations, draft]);

  const pickerTeeth = toothPickerFor
    ? (pendingRef.current.get(toothPickerFor)?.teeth ??
      teethByInvestigation.get(toothPickerFor) ??
      [])
    : [];

  return (
    <div
      style={{
        padding: 'var(--sp-4) var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      {/* ── Suggested from Chart ──
          Preselects a TOOTH only. The investigation type is always chosen by
          the clinician; nothing here proposes one. */}
      {suggestions.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--text-muted)',
            }}
          >
            Suggested from Chart
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
            {suggestions.map((s) => (
              <button
                key={s.ref}
                onClick={() => startDraft([s.tooth])}
                title="Start an investigation on this tooth — you still choose the type"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 10px',
                  borderRadius: 'var(--radius-full)',
                  border: `1px dashed ${primary}`,
                  background: 'transparent',
                  color: primary,
                  fontSize: 'var(--font-size-xs)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <span style={{ fontWeight: 600 }}>+</span>
                {[s.tooth, s.surfaces || null, s.label].filter(Boolean).join(' · ')}
              </button>
            ))}
          </div>
        </div>
      )}

      {displayRows.length === 0 && (
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          No investigations recorded for this visit.
        </div>
      )}

      {displayRows.map(({ row, isDraft }) => {
        const teeth = isDraft ? (draft?.teeth ?? []) : (teethByInvestigation.get(row.id) ?? []);
        const summary = summarizeDiagnosisTeeth([...teeth]);
        return (
          <div
            key={row.id}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              paddingBottom: 'var(--sp-2)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <div
              className="pc-finding-row"
              style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-2)' }}
            >
              {/* TOOTH CONTEXT — "General" is presentation for no association;
                  no fake tooth value is ever stored. */}
              <button
                className="pc-finding-context"
                onClick={() => setToothPickerFor(row.id)}
                title={
                  summary.hiddenCount > 0
                    ? `Teeth: ${summary.fullLabel}`
                    : 'Choose teeth for this investigation'
                }
                aria-label={
                  summary.all.length > 0
                    ? `Teeth ${summary.fullLabel}. Choose teeth`
                    : 'No tooth association. Choose teeth'
                }
                style={{
                  padding: 0,
                  paddingTop: FIRST_LINE_OFFSET,
                  border: 'none',
                  background: 'transparent',
                  color: summary.label ? 'var(--text)' : 'var(--text-muted)',
                  fontSize: 'var(--font-size-xs)',
                  lineHeight: 'var(--line-height-body)',
                  fontWeight: summary.label ? 600 : 400,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                  alignSelf: 'flex-start',
                  borderBottom: isDraft ? '1px dashed var(--border)' : undefined,
                }}
              >
                {summary.label || 'General'}
              </button>

              {/* INVESTIGATION TYPE — a typeahead over the common radiographs,
                  but free text underneath: any term may be saved. */}
              <div style={{ width: 150, flexShrink: 0 }}>
                <InvestigationTypeInput
                  identity={row.id}
                  storedValue={resolveInvestigationType(row)}
                  onSave={saveType}
                  autoFocus={isDraft}
                />
              </div>

              {/* FINDINGS — autosaving, dictating, explicit-newline sizing. */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {isDraft ? (
                  <span
                    style={{
                      display: 'block',
                      paddingTop: FIRST_LINE_OFFSET,
                      fontSize: 'var(--font-size-xs)',
                      lineHeight: 'var(--line-height-body)',
                      color: 'var(--text-placeholder, var(--text-muted))',
                    }}
                  >
                    Findings can be added once the investigation is saved
                  </span>
                ) : (
                  <HistoryEntryEditor
                    identity={`${row.id}:findings`}
                    storedValue={row.findings ?? ''}
                    onSave={(text) => saveFindings(text, row.id)}
                    label="Investigation findings"
                    placeholder="Findings…"
                    voiceEnabled={voiceSettings.voiceToTextEnabled}
                    autoRowsMax={4}
                  />
                )}
              </div>

              <button
                onClick={() => void removeInvestigation(row.id)}
                aria-label="Remove investigation"
                title="Remove investigation"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  marginTop: FIRST_LINE_OFFSET + 4,
                  padding: 4,
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                }}
              >
                <NavIcon name="x" />
              </button>
            </div>
          </div>
        );
      })}

      <div>
        <button
          onClick={() => startDraft([])}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 'var(--radius-sm)',
            border: `1px solid ${primary}`,
            background: 'transparent',
            color: primary,
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-medium)',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          <NavIcon name="plus" />
          Add Investigation
        </button>
      </div>

      {toothPickerFor && (
        <ToothPickerModal
          open
          initialTeeth={pickerTeeth.map((tooth) => ({ tooth, surfaces: [] }))}
          toothStates={toothStates}
          primary={primary}
          // An investigation is taken of a tooth, not of a surface.
          showSurfaces={false}
          onClose={() => setToothPickerFor(null)}
          onSave={(picked) => {
            const teeth = picked.map((t) => t.tooth);
            const pending = pendingRef.current.get(toothPickerFor);
            if (pending) {
              pending.teeth = teeth;
              setDraft((d) => (d?.id === toothPickerFor ? { ...d, teeth } : d));
            } else {
              void saveTeeth(toothPickerFor, teeth);
            }
            setToothPickerFor(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * Compact investigation-type field: a typeahead over the common dental
 * radiographs, backed by free text.
 *
 * The list is a convenience, never a constraint — "Other" needs no special
 * handling because whatever the clinician types IS the type, and `findings` is
 * never overloaded with the investigation's name.
 */
function InvestigationTypeInput({
  identity,
  storedValue,
  onSave,
  autoFocus,
}: {
  identity: string;
  storedValue: string;
  onSave: (value: string, identity: string) => void;
  autoFocus?: boolean;
}) {
  const [value, setValue] = useState(storedValue);
  const [open, setOpen] = useState(false);
  const matches = useMemo(() => (open ? searchInvestigationTypes(value) : []), [open, value]);

  const commit = (next: string) => {
    const trimmed = next.trim();
    setValue(trimmed);
    setOpen(false);
    if (trimmed && trimmed !== storedValue) onSave(trimmed, identity);
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        autoFocus={autoFocus}
        type="text"
        value={value}
        placeholder="Type…"
        onChange={(e) => {
          setValue(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          setOpen(false);
          commit(value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit(value);
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '6px 8px',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--border)',
          outline: 'none',
          background: 'var(--bg-surface)',
          fontSize: 'var(--font-size-xs)',
          lineHeight: 'var(--line-height-body)',
          color: 'var(--text)',
          fontFamily: 'inherit',
        }}
      />
      {open && matches.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 20,
            marginTop: 4,
            minWidth: 180,
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
          }}
        >
          {matches.map((t) => (
            <button
              key={t}
              type="button"
              // mousedown, not click: blur would close the list first.
              onMouseDown={(e) => {
                e.preventDefault();
                commit(t);
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '6px 10px',
                border: 'none',
                background: 'transparent',
                color: 'var(--text)',
                fontSize: 'var(--font-size-xs)',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {t}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
